import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { dayjsServer } from "@/helpers/dayjsServer"
import { ServerDate } from "@/helpers/server-date.class"
import { useServerTimer } from "@/hooks/useServerTimer"
import { useGetGameplayStateQuery } from "@/store/apis/gameplayAPI"
import { useGetMatchSessionQuery } from "@/store/apis/matchAPI"
import { useGetPlayerProfileQuery } from "@/store/apis/playerAPI"
import { useAppSelector } from "@/store/index"
import { GameplaySelectors } from "@/store/slices/gameplay"

export const useGameTimer = () => {
  const { sessionId: sessionIdParam } = useParams<"sessionId">()

  const sessionIdTgParam = window.location.search.split("match_")[1]

  const sessionIdStore = useAppSelector(GameplaySelectors.selectSessionId())

  const sessionId = sessionIdTgParam || sessionIdParam || sessionIdStore

  const { seconds: remainingTurnSeconds, restart } = useServerTimer({
    expiryTimestamp: new ServerDate(),
    autoStart: false
  })

  const { data: playerProfile } = useGetPlayerProfileQuery()

  const { data: matchSession } = useGetMatchSessionQuery(
    { sessionId: sessionId ?? "", playerId: playerProfile?._id ?? "" },
    {
      skip: !sessionId || !playerProfile?._id
    }
  )

  const isMatchActive = matchSession?.status === "ACTIVE"

  const { data: gameStateData } = useGetGameplayStateQuery(
    { sessionId: sessionId ?? "" },
    {
      skip: !isMatchActive
    }
  )

  const gameState = useMemo(() => {
    return isMatchActive ? gameStateData : matchSession?.snapshot?.state
  }, [isMatchActive, matchSession, gameStateData])

  useEffect(() => {
    if (!gameState?.turnStartedAt || !gameState?.moveTimeMs || !isMatchActive) {
      return
    }

    restart(
      dayjsServer(gameState.turnStartedAt)
        .add(gameState.moveTimeMs - 500, "millisecond")
        .toDate()
    )
  }, [gameState?.moveTimeMs, gameState?.turnStartedAt, isMatchActive])

  return {
    remainingTurnSeconds
  }
}
